import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import trashIcon from '../assets/icons/trash.svg';
import { Container, Row, Col, Card, Button, Alert, Badge } from 'react-bootstrap';
import { formatDate } from '../utils/dateFormatter';
import { formatPaymentMethod } from '../utils/paymentMethodFormatter';
import { CustomerLink, TransactionLink, RTOLink } from './EntityLinks';
import { DeleteConfirmModal } from './shared/DeleteConfirmModal';

/**
 * PaymentDetail component displays details of a specific payment
 */
export function PaymentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { payments, setPayments, apiCall, user } = useOutletContext();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadPayment = async () => {
      const paymentId = parseInt(id);
      // Show the cached list row instantly, but always fetch the detail so
      // reconciliation match fields (only returned by GET /payments/:id) load.
      const cached = payments.find(p => p.id === paymentId);
      if (cached && !cancelled) setPayment(cached);

      try {
        const response = await apiCall(`${API_BASE_URL}/payments/${paymentId}`);
        const foundPayment = response.data || response;
        if (cancelled) return;
        if (foundPayment && foundPayment.id) {
          setPayment(foundPayment);
        } else if (!cached) {
          setError('Payment not found');
        }
      } catch (err) {
        if (!cancelled && !cached) {
          setError('Failed to load payment: ' + err.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPayment();
    return () => { cancelled = true; };
  }, [id, payments, apiCall]);

  const handleResendReceipt = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiCall(
        `${API_BASE_URL}/payments/${payment.id}/resend-receipt`,
        { method: 'POST' }
      );

      alert(response.data?.message || 'Receipt sent successfully!');
    } catch (err) {
      setError('Failed to send receipt: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await apiCall(`${API_BASE_URL}/payments/${payment.id}`, { method: 'DELETE' });
      setShowDeleteModal(false);
      const paymentsRes = await apiCall(`${API_BASE_URL}/payments`);
      setPayments(paymentsRes?.data || []);
      navigate('/dashboard/payments');
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Use joined data from API response, fallback to context lookup
  const getCustomerName = (pmt) => {
    if (pmt.first_name && pmt.last_name) {
      return `${pmt.first_name} ${pmt.last_name}`;
    }
    return `Customer #${pmt.customer_id}`;
  };

  if (loading) {
    return <div className="loading">Loading payment details...</div>;
  }

  if (error && !payment) {
    return (
      <section className="payment-detail">
        <button onClick={() => navigate('/dashboard/payments')} className="back-btn">
          ← Back to Payments
        </button>
        <div className="error-message">{error}</div>
      </section>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-3">
        <Col>
          <Button variant="link" onClick={() => navigate('/dashboard/payments')} className="p-0">
            ← Back to Payments
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <h2>
            Payment #{payment.id}
            <span style={{ fontSize: '0.8em', marginLeft: '10px', color: '#666' }}>
              ${payment.amount}
            </span>
          </h2>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Header>
          <h3>Payment Information</h3>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6} className="mb-3">
              <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Customer</strong>
              <CustomerLink
                customerId={payment.customer_id}
                customerName={getCustomerName(payment)}
              />
            </Col>
            <Col md={6} className="mb-3">
              <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Amount</strong>
              <span>${payment.amount}</span>
            </Col>
            <Col md={6} className="mb-3">
              <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Payment Method</strong>
              <span>{formatPaymentMethod(payment.payment_method)}</span>
            </Col>
            <Col md={6} className="mb-3">
              <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Payment Date</strong>
              <span>{formatDate(payment.payment_date)}</span>
            </Col>
            <Col md={6} className="mb-3">
              <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>
                {!payment.transaction_id && payment.rto_agreement_id ? 'RTO Agreement' : 'Transaction ID'}
              </strong>
              {payment.transaction_id ? (
                <TransactionLink transactionId={payment.transaction_id}>
                  #{payment.transaction_id}
                </TransactionLink>
              ) : payment.rto_agreement_id ? (
                <>
                  <RTOLink rtoId={payment.rto_agreement_id}>
                    Lease #{payment.rto_agreement_id}
                  </RTOLink>
                  {payment.rto_vehicle_make && (
                    <span style={{ fontSize: '0.75rem', color: '#666', marginLeft: '0.5rem' }}>
                      ({payment.rto_vehicle_make} {payment.rto_vehicle_model})
                    </span>
                  )}
                </>
              ) : (
                <span>(orphaned)</span>
              )}
            </Col>
            <Col md={6} className="mb-3">
              <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Agent</strong>
              <span>{payment.agent_name || `Agent ID: ${payment.agent_id}`}</span>
            </Col>
          </Row>
          <div className="mt-3">
            <Button
              variant="info"
              onClick={handleResendReceipt}
              disabled={loading}
              className="me-2"
            >
              📧 Resend Receipt
            </Button>
            {user?.role !== 'auditor' && (
              <Button
                variant="danger"
                onClick={handleDelete}
              >
                <img src={trashIcon} alt="" className="icon-btn" />
                Delete Payment
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>

      {payment.match_id && (
        <Card className="mt-3 border-success">
          <Card.Header className="d-flex align-items-center">
            <h3 className="mb-0">Reconciliation Match</h3>
            <Badge bg="success" className="ms-2">Matched</Badge>
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
              Reconciled from an extracted {formatPaymentMethod(payment.match_payment_type)} payment notification.
            </p>
            <Row>
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Source</strong>
                <span>{formatPaymentMethod(payment.match_payment_type)}</span>
              </Col>
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Sender Name</strong>
                <span>{payment.match_sender_name || '-'}</span>
              </Col>
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Extracted Amount</strong>
                <span>${payment.match_amount}</span>
              </Col>
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Notification Date</strong>
                <span>{formatDate(payment.match_payment_date)}</span>
              </Col>
              {payment.match_transaction_number && (
                <Col md={6} className="mb-3">
                  <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Confirmation #</strong>
                  <span>{payment.match_transaction_number}</span>
                </Col>
              )}
              {payment.match_memo && (
                <Col md={6} className="mb-3">
                  <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Memo</strong>
                  <span>{payment.match_memo}</span>
                </Col>
              )}
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Matched By</strong>
                <span>{payment.match_matched_by_agent_name || '-'}</span>
              </Col>
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Matched At</strong>
                <span>{formatDate(payment.match_matched_at)}</span>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <DeleteConfirmModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        itemLabel="payment"
        error={deleteError}
        isDeleting={isDeleting}
      />
    </Container>
  );
}

export default PaymentDetail;
